import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, Download, Mail, Edit, FileText } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const yen = (n) => `¥${(Number(n) || 0).toLocaleString()}`;

const downloadPdf = async (q, headers) => {
  try {
    const res = await axios.get(`${API}/admin/quotes/${q.id}/pdf`, { headers, responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${q.quoteNumber}_見積書.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  } catch (e) { toast.error('PDFのダウンロードに失敗しました'); }
};

const ItemsEditor = ({ items, setItems }) => (
  <div className="space-y-2">
    {items.map((it, i) => (
      <div key={i} className="flex gap-2" data-testid={`quote-item-${i}`}>
        <Input value={it.name} placeholder="品目" className="flex-1" data-testid={`quote-item-name-${i}`}
          onChange={(e) => setItems(items.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} />
        <Input type="number" value={it.amount} className="w-32 text-right" data-testid={`quote-item-amount-${i}`}
          onChange={(e) => setItems(items.map((x, j) => j === i ? { ...x, amount: parseInt(e.target.value, 10) || 0 } : x))} />
        <Button variant="ghost" size="sm" className="text-red-600" disabled={items.length === 1} data-testid={`quote-item-remove-${i}`}
          onClick={() => setItems(items.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4" /></Button>
      </div>
    ))}
    <Button variant="outline" size="sm" data-testid="quote-item-add" onClick={() => setItems([...items, { name: '', amount: 0 }])}>
      <Plus className="h-4 w-4 mr-1" />品目を追加
    </Button>
  </div>
);

const QuoteForm = ({ form, setForm }) => {
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const total = form.items.reduce((s, i) => s + (Number(i.amount) || 0), 0);
  return (
    <div className="space-y-3 text-sm">
      <div className="grid grid-cols-2 gap-3">
        <div><Label>会社名</Label><Input value={form.companyName} onChange={set('companyName')} data-testid="quote-company" /></div>
        <div><Label>氏名</Label><Input value={form.userName} onChange={set('userName')} data-testid="quote-name" /></div>
        <div><Label>メールアドレス</Label><Input value={form.userEmail} onChange={set('userEmail')} data-testid="quote-email" /></div>
        <div><Label>有効期限</Label><Input type="date" value={form.validUntil} onChange={set('validUntil')} data-testid="quote-valid-until" /></div>
      </div>
      <div><Label>講習名</Label><Input value={form.courseName} onChange={set('courseName')} data-testid="quote-course" /></div>
      <div className="flex gap-4 items-center">
        <Label>宛先の表示</Label>
        <select value={form.recipientDisplay} onChange={set('recipientDisplay')} className="border rounded-md px-2 py-1 bg-white" data-testid="quote-recipient-display">
          <option value="both">会社名＋氏名</option><option value="company">会社名のみ</option><option value="name">氏名のみ</option>
        </select>
        <label className="flex items-center gap-1"><input type="checkbox" checked={form.hideParticipants} data-testid="quote-hide-participants"
          onChange={(e) => setForm({ ...form, hideParticipants: e.target.checked })} />受講者名を載せない</label>
      </div>
      <div><Label>品目・金額（税込）</Label><ItemsEditor items={form.items} setItems={(items) => setForm({ ...form, items })} /></div>
      <div className="text-right font-semibold" data-testid="quote-total">合計：{yen(total)}</div>
      <div><Label>備考</Label><Textarea rows={2} value={form.note} onChange={set('note')} data-testid="quote-note" /></div>
    </div>
  );
};

const QuoteRow = ({ q, headers, onEdit, onChanged }) => {
  const [sending, setSending] = useState(false);
  const send = async () => {
    if (!window.confirm(`${q.userEmail} に見積書をメールで送信しますか？`)) return;
    setSending(true);
    try { await axios.post(`${API}/admin/quotes/${q.id}/send-email`, {}, { headers }); toast.success('メールを送信しました'); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || 'メール送信に失敗しました'); }
    finally { setSending(false); }
  };
  const remove = async () => {
    if (!window.confirm(`見積書 ${q.quoteNumber} を削除しますか？`)) return;
    try { await axios.delete(`${API}/admin/quotes/${q.id}`, { headers }); toast.success('削除しました'); onChanged(); }
    catch (e) { toast.error('削除に失敗しました'); }
  };
  return (
    <div className="flex items-center gap-2 border rounded-md px-3 py-2 text-sm" data-testid={`quote-row-${q.id}`}>
      <div className="flex-1">
        <div className="font-medium">{q.quoteNumber}　{yen(q.totalAmount)}</div>
        <div className="text-xs text-gray-500">発行 {String(q.createdAt).slice(0, 10)} ／ 有効期限 {q.validUntil || '-'}{q.emailSentAt ? ` ／ メール送信済 ${String(q.emailSentAt).slice(0, 10)}` : ''}</div>
      </div>
      <Button variant="ghost" size="sm" title="PDF" onClick={() => downloadPdf(q, headers)} data-testid={`quote-pdf-${q.id}`}><Download className="h-4 w-4" /></Button>
      <Button variant="ghost" size="sm" title="メール送信" disabled={sending || !q.userEmail} onClick={send} data-testid={`quote-mail-${q.id}`}><Mail className="h-4 w-4" /></Button>
      <Button variant="ghost" size="sm" title="編集" onClick={() => onEdit(q)} data-testid={`quote-edit-${q.id}`}><Edit className="h-4 w-4" /></Button>
      <Button variant="ghost" size="sm" className="text-red-600" title="削除" onClick={remove} data-testid={`quote-delete-${q.id}`}><Trash2 className="h-4 w-4" /></Button>
    </div>
  );
};

// 申込から見積書を作成・編集し、PDFダウンロード／メール送信する
export const QuoteDialog = ({ app, onClose, getAuthHeaders }) => {
  const headers = getAuthHeaders();
  const [quotes, setQuotes] = useState([]);
  const [form, setForm] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [sendEmail, setSendEmail] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const res = await axios.get(`${API}/admin/quotes`, { params: { applicationId: app.id }, headers });
    setQuotes(res.data);
    return res.data;
  };
  const startNew = async () => {
    const res = await axios.get(`${API}/admin/quotes/draft`, { params: { applicationId: app.id }, headers });
    setForm(res.data); setEditingId(null); setSendEmail(false);
  };
  useEffect(() => {
    if (!app) return;
    setForm(null);
    load().then((list) => { if (!list.length) startNew(); }).catch(() => toast.error('見積書の読み込みに失敗しました'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [app?.id]);

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...form, applicationId: app.id, sendEmail: !editingId && sendEmail };
      const res = editingId ? await axios.put(`${API}/admin/quotes/${editingId}`, body, { headers }) : await axios.post(`${API}/admin/quotes`, body, { headers });
      toast.success(editingId ? '見積書を更新しました' : `見積書 ${res.data.quoteNumber} を作成しました${body.sendEmail ? '（メール送信済）' : ''}`);
      setForm(null); setEditingId(null);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || '保存に失敗しました'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!app} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="quote-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><FileText className="h-5 w-5 text-[#00A651]" />見積書　{app?.company ? `${app.company} ` : ''}{app?.name} 様</DialogTitle></DialogHeader>
        {quotes.length > 0 && (
          <div className="space-y-2" data-testid="quote-list">
            {quotes.map((q) => <QuoteRow key={q.id} q={q} headers={headers} onChanged={load}
              onEdit={(x) => { setForm({ ...x }); setEditingId(x.id); }} />)}
          </div>
        )}
        {form ? (
          <div className="border-t pt-3">
            <div className="font-semibold mb-2 text-[#00A651]">{editingId ? `${form.quoteNumber} を編集` : '新しい見積書'}</div>
            <QuoteForm form={form} setForm={setForm} />
          </div>
        ) : (
          <Button variant="outline" onClick={startNew} data-testid="quote-new-btn"><Plus className="h-4 w-4 mr-1" />新しい見積書を作成</Button>
        )}
        {form && (
          <DialogFooter className="flex items-center gap-3">
            {!editingId && (
              <label className="flex items-center gap-1 text-sm mr-auto"><input type="checkbox" checked={sendEmail} disabled={!form.userEmail}
                onChange={(e) => setSendEmail(e.target.checked)} data-testid="quote-send-email-check" />作成と同時にメールで送る</label>
            )}
            <Button variant="outline" onClick={() => { setForm(null); setEditingId(null); }} data-testid="quote-cancel-btn">キャンセル</Button>
            <Button className="bg-[#00A651] hover:bg-[#008a43]" disabled={saving} onClick={save} data-testid="quote-save-btn">
              {saving ? '保存中…' : editingId ? '更新する' : '見積書を作成'}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default QuoteDialog;
